import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';

export interface SeoConfig {
  title: string;
  description: string;
  canonicalPath?: string;
  image?: string;
  robots?: 'index,follow' | 'noindex,nofollow';
  type?: 'website' | 'product';
}

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly defaultImage = `${environment.SITE_URL}/assets/img/default-image/Logo_header.png`;
  private readonly productSchemaId = 'kb-imitation-product-schema';

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  update(config: SeoConfig): void {
    const canonicalUrl = this.getAbsoluteUrl(config.canonicalPath || '/');
    const imageUrl = this.getAbsoluteUrl(config.image || this.defaultImage);
    const robots = config.robots || 'index,follow';

    this.title.setTitle(config.title);
    this.meta.updateTag({ name: 'description', content: config.description });
    this.meta.updateTag({ name: 'robots', content: robots });
    this.meta.updateTag({ property: 'og:type', content: config.type || 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'KB Imitation' });
    this.meta.updateTag({ property: 'og:title', content: config.title });
    this.meta.updateTag({ property: 'og:description', content: config.description });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: config.title });
    this.meta.updateTag({ name: 'twitter:description', content: config.description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
    this.setCanonical(canonicalUrl);

    if (config.type !== 'product') {
      this.clearProductSchema();
    }
  }

  updateProduct(product: any, canonicalPath: string): void {
    const name = String(product?.name || 'Imitation Jewellery');
    const description = this.truncate(String(product?.description || `Shop ${name} from KB Imitation.`), 160);
    const image = this.getProductImage(product?.profileImage);
    const price = Number(product?.discount > 0
      ? Number(product?.price || 0) - (Number(product?.price || 0) * Number(product?.discount || 0)) / 100
      : Number(product?.price || 0));

    this.update({
      title: `${name} | KB Imitation`,
      description,
      canonicalPath,
      image,
      type: 'product',
    });

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name,
      description,
      image: [this.getAbsoluteUrl(image)],
      sku: String(product?._id || ''),
      brand: { '@type': 'Brand', name: 'KB Imitation' },
      category: String(product?.category?.[0]?.name || ''),
      offers: {
        '@type': 'Offer',
        url: this.getAbsoluteUrl(canonicalPath),
        priceCurrency: 'INR',
        price: Number.isFinite(price) ? price.toFixed(2) : '0.00',
        availability: Number(product?.quantity || 0) > 0
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      },
    };

    this.setProductSchema(schema);
  }

  private setCanonical(url: string): void {
    let canonical = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      this.document.head.appendChild(canonical);
    }
    canonical.href = url;
  }

  private setProductSchema(schema: Record<string, unknown>): void {
    this.clearProductSchema();
    const script = this.document.createElement('script');
    script.id = this.productSchemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    this.document.head.appendChild(script);
  }

  private clearProductSchema(): void {
    this.document.getElementById(this.productSchemaId)?.remove();
  }

  private getProductImage(image: unknown): string {
    const imagePath = String(image || '').trim();
    if (!imagePath) return this.defaultImage;
    return /^https?:\/\//i.test(imagePath) ? imagePath : `${environment.IMAGE_URL}${imagePath}`;
  }

  private getAbsoluteUrl(pathOrUrl: string): string {
    return new URL(pathOrUrl, environment.SITE_URL).toString();
  }

  private truncate(value: string, limit: number): string {
    const cleanValue = value.replace(/\s+/g, ' ').trim();
    return cleanValue.length > limit ? `${cleanValue.slice(0, limit - 1).trim()}…` : cleanValue;
  }
}
